import React, { useState, useEffect, useCallback } from 'react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, addMonths, subMonths, startOfWeek, endOfWeek, addWeeks, subWeeks, addDays, subDays, parseISO } from 'date-fns';
import { ar } from 'date-fns/locale';
import Layout from '../../components/ui/Layout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import SubHeader from '../../components/ui/SubHeader';
import ActionBoxes from '../../components/ui/ActionBoxes';
import AppointmentFilters from './components/AppointmentFilters';
import AppointmentDetails from './components/AppointmentDetails';
import AppointmentBookingModal from './components/AppointmentBookingModal';
import StaffAvailabilityMatrix from './components/StaffAvailabilityMatrix';
import { useAuth } from '../../contexts/AuthContext';
import { appointmentService } from '../../services/appointmentService';
import { useAppointmentRealtime } from '../../hooks/useAppointmentRealtime';
import { reminderService } from '../../services/reminderService';
import AllPropertiesModal from './components/AllPropertiesModal';
import AllClientsModal from './components/AllClientsModal';



const CalendarAndAppointmentScheduling = () => {
  const { user } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // 'month', 'week', 'day'
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const [isStaffMatrixOpen, setIsStaffMatrixOpen] = useState(false);
  const [showReminderPanel, setShowReminderPanel] = useState(false);
  const [isQuickMenuOpen, setIsQuickMenuOpen] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    propertyType: 'all',
    staffMember: 'all',
    clientStatus: 'all'
  });
  const [isAllPropertiesModalOpen, setIsAllPropertiesModalOpen] = useState(false);
  const [isAllClientsModalOpen, setIsAllClientsModalOpen] = useState(false);

  // Load appointments from Supabase
  const loadAppointments = useCallback(async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      const data = await appointmentService?.getAll(user?.id);
      
      // Transform to match component format
      const transformedData = data?.map(apt => ({
        id: apt?.id,
        propertyId: apt?.propertyId,
        propertyAddress: apt?.propertyTitle || 'عنوان غير محدد',
        clientName: apt?.clientName || 'عميل غير محدد',
        clientPhone: '',
        staffName: user?.email || 'موظف',
        staffId: user?.id,
        date: apt?.appointmentDate ? format(new Date(apt.appointmentDate), 'yyyy-MM-dd') : '',
        time: apt?.appointmentDate ? format(new Date(apt.appointmentDate), 'HH:mm') : '',
        duration: apt?.duration || 60,
        status: apt?.status || 'scheduled',
        type: 'viewing',
        notes: apt?.description || '',
        reminderSent: apt?.reminderSent || false
      })) || [];
      
      setAppointments(transformedData);
      setError('');
    } catch (err) {
      console.error('Error loading appointments:', err);
      setError(err?.message || 'فشل تحميل المواعيد');
    } finally {
      setLoading(false);
    }
  }, [user?.id, user?.email]);

  // Initial load
  useEffect(() => {
    loadAppointments();
  }, [loadAppointments]);

  // Real-time subscription handler
  const handleAppointmentChange = useCallback((eventType, payload) => {
    if (eventType === 'INSERT') {
      // New appointment added
      const newAppointment = {
        id: payload?.id,
        propertyId: payload?.property_id,
        propertyAddress: 'عنوان غير محدد',
        clientName: 'عميل غير محدد',
        clientPhone: '',
        staffName: user?.email || 'موظف',
        staffId: payload?.user_id,
        date: payload?.appointment_date ? format(new Date(payload.appointment_date), 'yyyy-MM-dd') : '',
        time: payload?.appointment_date ? format(new Date(payload.appointment_date), 'HH:mm') : '',
        duration: payload?.duration || 60,
        status: payload?.status || 'scheduled',
        type: 'viewing',
        notes: payload?.description || '',
        reminderSent: payload?.reminder_sent || false
      };
      
      setAppointments(prev => [...prev, newAppointment]);
    } else if (eventType === 'UPDATE') {
      // Appointment updated
      setAppointments(prev => prev?.map(apt => {
        if (apt?.id === payload?.id) {
          return {
            ...apt,
            date: payload?.appointment_date ? format(new Date(payload.appointment_date), 'yyyy-MM-dd') : apt?.date,
            time: payload?.appointment_date ? format(new Date(payload.appointment_date), 'HH:mm') : apt?.time,
            duration: payload?.duration || apt?.duration,
            status: payload?.status || apt?.status,
            notes: payload?.description || apt?.notes,
            reminderSent: payload?.reminder_sent || apt?.reminderSent
          };
        }
        return apt;
      }));
      
      // Update selected appointment if it's the one being updated
      if (selectedAppointment?.id === payload?.id) {
        setSelectedAppointment(prev => ({
          ...prev,
          date: payload?.appointment_date ? format(new Date(payload.appointment_date), 'yyyy-MM-dd') : prev?.date,
          time: payload?.appointment_date ? format(new Date(payload.appointment_date), 'HH:mm') : prev?.time,
          duration: payload?.duration || prev?.duration,
          status: payload?.status || prev?.status,
          notes: payload?.description || prev?.notes,
          reminderSent: payload?.reminder_sent || prev?.reminderSent
        }));
      }
    } else if (eventType === 'DELETE') {
      // Appointment deleted
      setAppointments(prev => prev?.filter(apt => apt?.id !== payload?.id));
      
      // Clear selected appointment if it's the one being deleted
      if (selectedAppointment?.id === payload?.id) {
        setSelectedAppointment(null);
      }
    }
  }, [user?.email, selectedAppointment?.id]);

  // Enable real-time subscription
  useAppointmentRealtime(user?.id, handleAppointmentChange);

  // Add function declarations before calendarActions
  const handleToday = () => {
    setCurrentDate(new Date());
  };

  // Calendar action boxes
  const calendarActions = [
    {
      id: 'new-appointment',
      icon: 'Plus',
      label: 'موعد جديد',
      color: '#3B82F6',
      bgGradient: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(96, 165, 250, 0.25) 100%)',
      borderColor: 'rgba(59, 130, 246, 0.5)',
      onClick: () => setIsBookingModalOpen(true)
    },
    {
      id: 'all-properties',
      icon: 'Home',
      label: 'جميع العقارات',
      color: '#10B981',
      bgGradient: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(52, 211, 153, 0.25) 100%)',
      borderColor: 'rgba(16, 185, 129, 0.5)',
      onClick: () => setIsAllPropertiesModalOpen(true)
    },
    {
      id: 'all-clients',
      icon: 'Users',
      label: 'جميع العملاء',
      color: '#F59E0B',
      bgGradient: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2) 0%, rgba(251, 191, 36, 0.25) 100%)',
      borderColor: 'rgba(245, 158, 11, 0.5)',
      onClick: () => setIsAllClientsModalOpen(true)
    },
    {
      id: 'month-view',
      icon: 'Calendar',
      label: 'عرض شهري',
      color: '#8B5CF6',
      bgGradient: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(167, 139, 250, 0.25) 100%)',
      borderColor: 'rgba(139, 92, 246, 0.5)',
      onClick: () => setViewMode('month')
    },
    {
      id: 'week-view',
      icon: 'CalendarDays',
      label: 'عرض أسبوعي',
      color: '#EF4444',
      bgGradient: 'linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(248, 113, 113, 0.25) 100%)',
      borderColor: 'rgba(239, 68, 68, 0.5)',
      onClick: () => setViewMode('week')
    },
    {
      id: 'day-view',
      icon: 'Clock',
      label: 'عرض يومي',
      color: '#06B6D4',
      bgGradient: 'linear-gradient(135deg, rgba(6, 182, 212, 0.2) 0%, rgba(34, 211, 238, 0.25) 100%)',
      borderColor: 'rgba(6, 182, 212, 0.5)',
      onClick: () => setViewMode('day')
    },
    {
      id: 'staff-availability',
      icon: 'UserCheck',
      label: 'توفر الموظفين',
      color: '#EC4899',
      bgGradient: 'linear-gradient(135deg, rgba(236, 72, 153, 0.2) 0%, rgba(244, 114, 182, 0.25) 100%)',
      borderColor: 'rgba(236, 72, 153, 0.5)',
      onClick: () => setIsStaffMatrixOpen(true)
    },
    {
      id: 'today',
      icon: 'CalendarCheck',
      label: 'اليوم',
      color: '#14B8A6',
      bgGradient: 'linear-gradient(135deg, rgba(20, 184, 166, 0.2) 0%, rgba(45, 212, 191, 0.25) 100%)',
      borderColor: 'rgba(20, 184, 166, 0.5)',
      onClick: handleToday
    }
  ];

  // Filter appointments based on selected filters
  const filteredAppointments = appointments?.filter(apt => {
    if (filters?.propertyType !== 'all') {
      const propertyType = apt?.propertyAddress?.includes('فيلا') ? 'villa' : 
                          apt?.propertyAddress?.includes('شقة') ? 'apartment' :
                          apt?.propertyAddress?.includes('مكتب') ? 'office' : 'shop';
      if (propertyType !== filters?.propertyType) return false;
    }
    if (filters?.staffMember !== 'all' && apt?.staffId !== filters?.staffMember) return false;
    if (filters?.clientStatus !== 'all' && apt?.status !== filters?.clientStatus) return false;
    return true;
  });

  // Get appointments for a specific date
  const getAppointmentsForDate = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return filteredAppointments?.filter(apt => apt?.date === dateStr);
  };

  // Navigation handlers
  const handlePrevious = () => {
    if (viewMode === 'month') setCurrentDate(subMonths(currentDate, 1));
    else if (viewMode === 'week') setCurrentDate(subWeeks(currentDate, 1));
    else setCurrentDate(subDays(currentDate, 1));
  };

  const handleNext = () => {
    if (viewMode === 'month') setCurrentDate(addMonths(currentDate, 1));
    else if (viewMode === 'week') setCurrentDate(addWeeks(currentDate, 1));
    else setCurrentDate(addDays(currentDate, 1));
  };

  const handleDateClick = (date) => {
    setSelectedDate(date);
    const dayAppointments = getAppointmentsForDate(date);
    if (dayAppointments?.length > 0) {
      setSelectedAppointment(dayAppointments?.[0]);
    } else {
      setSelectedAppointment(null);
    }
  };

  const handleAppointmentClick = (appointment) => {
    setSelectedAppointment(appointment);
    setSelectedDate(parseISO(appointment?.date));
  };

  const handleBookAppointment = (appointmentData) => {
    const newAppointment = {
      ...appointmentData,
      id: `apt${Date.now()}`,
      status: 'pending',
      reminderSent: false
    };
    setAppointments([...appointments, newAppointment]);
  };

  const handleUpdateAppointment = (updatedAppointment) => {
    setAppointments(appointments?.map(apt => 
      apt?.id === updatedAppointment?.id ? updatedAppointment : apt
    ));
    setSelectedAppointment(updatedAppointment);
    
    // Trigger reminder check when appointments update
    reminderService?.checkAndSendReminders(appointments);
  };

  const handleDeleteAppointment = (appointmentId) => {
    setAppointments(appointments?.filter(apt => apt?.id !== appointmentId));
    setSelectedAppointment(null);
  };

  // Render calendar views
  const renderMonthView = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const calendarStart = startOfWeek(monthStart, { locale: ar });
    const calendarEnd = endOfWeek(monthEnd, { locale: ar });
    const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

    const weekDays = ['الأحد', 'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];

    return (
      <div className="bg-card rounded-lg border border-border">
        {/* Week day headers */}
        <div className="grid grid-cols-7 border-b border-border">
          {weekDays?.map((day, idx) => (
            <div key={idx} className="p-3 text-center text-sm font-semibold text-muted-foreground bg-muted/30">
              {day}
            </div>
          ))}
        </div>
        {/* Calendar grid */}
        <div className="grid grid-cols-7">
          {days?.map((day, idx) => {
            const dayAppointments = getAppointmentsForDate(day);
            const isCurrentMonth = isSameMonth(day, currentDate);
            const isToday = isSameDay(day, new Date());
            const isSelected = selectedDate && isSameDay(day, selectedDate);

            return (
              <div
                key={idx}
                onClick={() => handleDateClick(day)}
                className={`min-h-[100px] p-2 border-b border-r border-border cursor-pointer transition-colors hover:bg-muted/20 ${
                  !isCurrentMonth ? 'bg-muted/10 text-muted-foreground' : ''
                } ${
                  isToday ? 'bg-primary/5' : ''
                } ${
                  isSelected ? 'ring-2 ring-primary ring-inset' : ''
                }`}
              >
                <div className={`text-sm font-semibold mb-1 ${
                  isToday ? 'text-primary' : ''
                }`}>
                  {format(day, 'd', { locale: ar })}
                </div>
                <div className="space-y-1">
                  {dayAppointments?.slice(0, 3)?.map(apt => (
                    <div
                      key={apt?.id}
                      onClick={(e) => {
                        e?.stopPropagation();
                        handleAppointmentClick(apt);
                      }}
                      className={`text-xs p-1 rounded truncate ${
                        apt?.status === 'confirmed' ? 'bg-success/20 text-success' :
                        apt?.status === 'pending'? 'bg-warning/20 text-warning' : 'bg-error/20 text-error'
                      }`}
                    >
                      {apt?.time} - {apt?.clientName}
                    </div>
                  ))}
                  {dayAppointments?.length > 3 && (
                    <div className="text-xs text-muted-foreground text-center">
                      +{dayAppointments?.length - 3} المزيد
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    const weekStart = startOfWeek(currentDate, { locale: ar });
    const weekEnd = endOfWeek(currentDate, { locale: ar });
    const days = eachDayOfInterval({ start: weekStart, end: weekEnd });
    const hours = Array.from({ length: 14 }, (_, i) => i + 7); // 7 AM to 8 PM

    return (
      <div className="bg-card rounded-lg border border-border overflow-auto">
        <div className="grid grid-cols-8 min-w-[800px]">
          {/* Time column header */}
          <div className="sticky right-0 bg-muted/30 border-b border-border p-3 text-sm font-semibold text-center">
            الوقت
          </div>
          {/* Day headers */}
          {days?.map((day, idx) => {
            const isToday = isSameDay(day, new Date());
            return (
              <div
                key={idx}
                className={`p-3 text-center border-b border-r border-border ${
                  isToday ? 'bg-primary/5' : 'bg-muted/30'
                }`}
              >
                <div className={`text-sm font-semibold ${
                  isToday ? 'text-primary' : 'text-foreground'
                }`}>
                  {format(day, 'EEEE', { locale: ar })}
                </div>
                <div className="text-xs text-muted-foreground">
                  {format(day, 'd MMM', { locale: ar })}
                </div>
              </div>
            );
          })}

          {/* Time slots */}
          {hours?.map((hour) => (
            <React.Fragment key={hour}>
              <div className="sticky right-0 bg-muted/20 border-b border-border p-2 text-xs text-center text-muted-foreground">
                {hour}:00
              </div>
              {days?.map((day, dayIdx) => {
                const dayAppointments = getAppointmentsForDate(day)?.filter(apt => {
                  const aptHour = parseInt(apt?.time?.split(':')?.[0]);
                  return aptHour === hour;
                });

                return (
                  <div
                    key={dayIdx}
                    className="min-h-[60px] p-1 border-b border-r border-border hover:bg-muted/10 cursor-pointer"
                    onClick={() => handleDateClick(day)}
                  >
                    {dayAppointments?.map(apt => (
                      <div
                        key={apt?.id}
                        onClick={(e) => {
                          e?.stopPropagation();
                          handleAppointmentClick(apt);
                        }}
                        className={`text-xs p-2 rounded mb-1 ${
                          apt?.status === 'confirmed' ? 'bg-success/20 text-success border border-success/30' :
                          apt?.status === 'pending'? 'bg-warning/20 text-warning border border-warning/30' : 'bg-error/20 text-error border border-error/30'
                        }`}
                      >
                        <div className="font-semibold truncate">{apt?.clientName}</div>
                        <div className="truncate opacity-80">{apt?.time}</div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  const renderDayView = () => {
    const hours = Array.from({ length: 14 }, (_, i) => i + 7); // 7 AM to 8 PM
    const dayAppointments = getAppointmentsForDate(currentDate);

    return (
      <div className="bg-card rounded-lg border border-border">
        <div className="p-4 border-b border-border bg-muted/30">
          <h3 className="text-lg font-semibold text-foreground">
            {format(currentDate, 'EEEE، d MMMM yyyy', { locale: ar })}
          </h3>
          <p className="text-sm text-muted-foreground mt-1">
            {dayAppointments?.length} موعد مجدول
          </p>
        </div>
        <div className="divide-y divide-border">
          {hours?.map((hour) => {
            const hourAppointments = dayAppointments?.filter(apt => {
              const aptHour = parseInt(apt?.time?.split(':')?.[0]);
              return aptHour === hour;
            });

            return (
              <div key={hour} className="flex">
                <div className="w-24 p-4 text-sm text-muted-foreground text-center bg-muted/20">
                  {hour}:00
                </div>
                <div className="flex-1 p-4 min-h-[80px]">
                  {hourAppointments?.length > 0 ? (
                    <div className="space-y-2">
                      {hourAppointments?.map(apt => (
                        <div
                          key={apt?.id}
                          onClick={() => handleAppointmentClick(apt)}
                          className={`p-3 rounded-lg border cursor-pointer hover:shadow-md transition-shadow ${
                            apt?.status === 'confirmed' ? 'bg-success/10 border-success/30' :
                            apt?.status === 'pending'? 'bg-warning/10 border-warning/30' : 'bg-error/10 border-error/30'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="font-semibold text-foreground">{apt?.clientName}</div>
                              <div className="text-sm text-muted-foreground mt-1">{apt?.propertyAddress}</div>
                              <div className="text-xs text-muted-foreground mt-1">
                                <Icon name="User" size={12} className="inline ml-1" />
                                {apt?.staffName}
                              </div>
                            </div>
                            <div className="text-sm font-semibold">{apt?.time}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground text-center py-4">
                      لا توجد مواعيد في هذا الوقت
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <Layout
      userRole="admin"
      userName="أحمد محمد"
      onQuickMenuToggle={() => setIsQuickMenuOpen(!isQuickMenuOpen)}
    >
      <SubHeader pageTitle="التقويم والمواعيد" />
      <ActionBoxes 
        actions={calendarActions} 
        showNewContractModal={false} 
        setShowNewContractModal={() => {}}
        contractTypeConfig={{}} // Add this line - required prop
      />
      <div className="min-h-screen bg-background p-4 md:p-6 lg:p-8">
        <div className="p-6 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-heading font-bold text-foreground">
                التقويم والمواعيد
              </h1>
              <p className="text-muted-foreground mt-1">
                إدارة مواعيد معاينة العقارات وتنسيق جداول الموظفين والعملاء
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                iconName="Users"
                onClick={() => setIsStaffMatrixOpen(true)}
              >
                جداول الموظفين
              </Button>
              <Button
                variant="default"
                iconName="Plus"
                onClick={() => setIsBookingModalOpen(true)}
              >
                حجز موعد جديد
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Left Sidebar - Filters */}
            <div className="lg:col-span-1">
              <AppointmentFilters
                filters={filters}
                onFiltersChange={setFilters}
                appointmentsCount={filteredAppointments?.length}
              />
            </div>

            {/* Main Calendar Area */}
            <div className="lg:col-span-2 space-y-4">
              {/* Calendar Controls */}
              <div className="flex items-center justify-between bg-card p-4 rounded-lg border border-border">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    iconName="ChevronRight"
                    onClick={handlePrevious}
                  />
                  <Button
                    variant="outline"
                    size="icon"
                    iconName="ChevronLeft"
                    onClick={handleNext}
                  />
                  <Button
                    variant="ghost"
                    onClick={handleToday}
                  >
                    اليوم
                  </Button>
                </div>

                <h2 className="text-xl font-heading font-bold text-foreground">
                  {viewMode === 'month' && format(currentDate, 'MMMM yyyy', { locale: ar })}
                  {viewMode === 'week' && `أسبوع ${format(currentDate, 'd MMM', { locale: ar })}`}
                  {viewMode === 'day' && format(currentDate, 'd MMMM yyyy', { locale: ar })}
                </h2>

                <div className="flex items-center gap-1 bg-muted/30 rounded-lg p-1">
                  <Button
                    variant={viewMode === 'month' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('month')}
                  >
                    شهر
                  </Button>
                  <Button
                    variant={viewMode === 'week' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('week')}
                  >
                    أسبوع
                  </Button>
                  <Button
                    variant={viewMode === 'day' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setViewMode('day')}
                  >
                    يوم
                  </Button>
                </div>
              </div>

              {/* Calendar View */}
              {viewMode === 'month' && renderMonthView()}
              {viewMode === 'week' && renderWeekView()}
              {viewMode === 'day' && renderDayView()}
            </div>

            {/* Right Panel - Appointment Details */}
            <div className="lg:col-span-1">
              <AppointmentDetails
                appointment={selectedAppointment}
                onUpdate={handleUpdateAppointment}
                onDelete={handleDeleteAppointment}
              />
            </div>
          </div>
        </div>

        {/* Modals */}
        <AppointmentBookingModal
          isOpen={isBookingModalOpen}
          onClose={() => setIsBookingModalOpen(false)}
          onBook={handleBookAppointment}
          selectedDate={selectedDate}
        />

        <StaffAvailabilityMatrix
          isOpen={isStaffMatrixOpen}
          onClose={() => setIsStaffMatrixOpen(false)}
          appointments={appointments}
        />

        <AllPropertiesModal
          isOpen={isAllPropertiesModalOpen}
          onClose={() => setIsAllPropertiesModalOpen(false)}
        />

        <AllClientsModal
          isOpen={isAllClientsModalOpen}
          onClose={() => setIsAllClientsModalOpen(false)}
        />
      </div>
    </Layout>
  );
};

export default CalendarAndAppointmentScheduling;